import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { FormikProvider, useFormik } from "formik";
import { Skeleton } from "primereact/skeleton";
import Error from "../../components/common/Error";
import CustomInputText from "../../components/forms/CustomInputText";
import { Card } from "primereact/card";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import { ScheduleRequest } from "../../models/dtos";
import CustomDropdown from "../../components/forms/CustomDropdown";
import { specialitiesApi } from "../../services/nomenclatures/specialitiesApi";
import { academicYearsApi } from "../../services/nomenclatures/academicYearsApi";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { scheduleApi } from "../../services/schedule/scheduleApi";
import { plansApi } from "../../services/plans/plansApi";
import { SelectButton } from "primereact/selectbutton";
import { sessionTypesApi } from "../../services/nomenclatures/sessionTypesApi";
import ScheduleSubjectsList from "./ScheduleSubjectsList";
import ItemBrowser from "../../components/forms/ItemBrowser";
import CustomCheckbox from "../../components/forms/CustomCheckbox";

const ScheduleDetails = () => {
  const { id } = useParams();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const options = ["Семестриално разписание", "Сесийно разписание"];

  const {
    data: schedule,
    error: scheduleError,
    isLoading: scheduleLoading,
  } = scheduleApi.useGetScheduleByIdQuery(parseInt(id!), {
    skip: !id,
  });

  const initialValue = schedule?.data.isSession ? "Сесийно разписание" : "Семестриално разписание";
  const [sessionValue, setSessionValue] = useState(initialValue);

  const [
    updateSchedule,
    { isLoading: isUpdateScheduleLoading, error: updateScheduleError, isSuccess: isUpdateScheduleSuccess },
  ] = scheduleApi.useUpdateScheduleMutation();

  const { data: specialities } = specialitiesApi.useGetSpecialitiesQuery();
  const { data: academicYears } = academicYearsApi.useGetAcademicYearsQuery();
  const [triggerPlansQuery] = plansApi.useLazyGetPlansByFacSpecQuery();
  const { data: sessionTypes } = sessionTypesApi.useGetSessionTypesQuery();

  const formik = useFormik({
    initialValues: {
      ...schedule?.data,
      scheduleSubjects: schedule?.data.scheduleSubjects.map((subject) => ({
        ...subject,
        subjectTimeTable: subject.subjectTimeTable?.map((timeTable) => ({
          ...timeTable,
          startDate: timeTable?.startDate ? new Date(timeTable.startDate) : null,
          endDate: timeTable.endDate ? new Date(timeTable.endDate) : null,
        })),
      })),
      startDate: schedule?.data.startDate ? new Date(schedule?.data.startDate) : null,
      endDate: schedule?.data.endDate ? new Date(schedule?.data.endDate) : null,
    } as Partial<ScheduleRequest>,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!values.scheduleId) {
        values.scheduleId = 0;
      }
      if (!values.sessionTypeId) {
        values.sessionTypeId = 0;
      }
      if (values.sessionTypeId) {
        values.isSession = true;
      }
      updateSchedule(values)
        .unwrap()
        .then((result) => {
          console.log(result);
          if (result && "data" in result) {
            navigate(`/schedule/${result.data}`);
          }
        });
    },
  });

  useEffect(() => {
    if (isUpdateScheduleSuccess) {
      toast.current?.show({ severity: "success", summary: "Обновяване", detail: "Учебното разписание е обновено." });
    }
  }, [isUpdateScheduleSuccess]);

  useEffect(() => {
    if (schedule?.data?.sessionTypeId && schedule.data.sessionTypeId > 0) {
      setSessionValue("Сесийно разписание");
    } else {
      setSessionValue("Семестриално разписание");
    }
  }, [schedule]);

  if (scheduleLoading) return <Skeleton className="w-full h-2rem" />;
  if (scheduleError) return <Error error={scheduleError} />;
  if (isUpdateScheduleLoading) return <ProgressSpinner />;
  if (updateScheduleError) return <Error error={updateScheduleError} />;

  return (
    <FormikProvider value={formik}>
      <div>
        <Toast ref={toast} />
        <form onSubmit={formik.handleSubmit}>
          <Card
            title={
              schedule?.data.specialityName && !schedule?.data.isSession
                ? `Учебно разписание / ${schedule.data.specialityName} / ${schedule.data.academicYearName}`
                : schedule?.data.isSession
                  ? `Сесийно разписание / ${schedule.data.specialityName} / ${schedule.data.academicYearName}`
                  : "Създаване на ново разписание"
            }
            footer={<Button type="submit" label="Save" icon="pi pi-save" />}
            className="mb-4 shadow-4"
            pt={{
              footer: {
                className: "flex justify-content-center",
              },
            }}
          >
            <div className="grid">
              <div className="card flex justify-content-center col-12">
                {schedule?.data.specialityName ? (
                  <></>
                ) : (
                  <SelectButton value={sessionValue} onChange={(e) => setSessionValue(e.value)} options={options} />
                )}
              </div>
              {sessionValue === "Сесийно разписание" && (
                <div className="col-12 md:col-4">
                  <CustomDropdown
                    id="sessionTypeId"
                    label="Тип сесия"
                    options={sessionTypes?.data || []}
                    optionLabel="name"
                    optionValue="sessionTypeId"
                    filter
                  />
                </div>
              )}
              <div className="col-12 md:col-8">
                <CustomDropdown
                  id="specialityId"
                  label="Специалност"
                  options={specialities?.data || []}
                  optionLabel="name"
                  optionValue="specialityId"
                  filter
                />
              </div>
              <div className="col-12 md:col-4">
                <CustomInputText id="semester" label="Семестър" />
              </div>
              <div className="col-12 md:col-4">
                <CustomDropdown
                  id="academicYearId"
                  label="Начална академична година"
                  options={academicYears?.data || []}
                  optionLabel="name"
                  optionValue="academicYearId"
                />
              </div>
              <div className={`field col-12 ${sessionValue === "Сесийно разписание" ? "md:col-4" : "md:col-8"}`}>
                <ItemBrowser
                  id="planId"
                  label="Учебен план"
                  optionsPromise={() =>
                    triggerPlansQuery({
                      specialityId: formik.values.specialityId,
                    })
                  }
                  idField="planId"
                  labelField="code"
                  currentLabelText={formik?.values.planCode}
                  columns={[
                    { field: "planId", header: "ID", sortable: true, filter: true },
                    { field: "code", header: "Код", sortable: true, filter: true },
                    {
                      field: "startAcademicYearYearNumber",
                      header: "Начална академична година",
                      sortable: true,
                      filter: true,
                    },
                    {
                      field: "endAcademicYearYearNumber",
                      header: "Крайна академична година",
                      sortable: true,
                      filter: true,
                    },
                    { field: "specialityName", header: "Специалност", sortable: true, filter: true },
                    { field: "degreeName", header: "Степен", sortable: true, filter: true },
                    { field: "planTypeName", header: "Тип", sortable: true, filter: true },
                    { field: "specialityModeName", header: "Форма на обучение", sortable: true, filter: true },
                  ]}
                />
              </div>
              {sessionValue === "Семестриално разписание" && (
                <>
                  <div className="field col-12 md:col-4">
                    <CustomDatepicker id="startDate" label="Начална дата семестър" />
                  </div>
                  <div className="field col-12 md:col-4">
                    <CustomDatepicker id="endDate" label="Крайна дата семестър" />
                  </div>
                </>
              )}

              <div className="field col-12">
                <CustomInputText id="comment" label="Коментар" type="multiline" />
              </div>
              <div className="field col-12 flex align-items-center">
                <CustomCheckbox id="isPublished" label="Публикуване на разписание" />
              </div>
            </div>
          </Card>
        </form>
        {schedule?.data?.scheduleSubjects && (
          <ScheduleSubjectsList
            schedule={schedule?.data}
            subjects={schedule?.data?.scheduleSubjects}
            specialityId={formik.values.specialityId}
            sessionTypeId={formik.values?.sessionTypeId}
          />
        )}
      </div>
    </FormikProvider>
  );
};

export default ScheduleDetails;
